import {
  ChallengingQuota,
  QuotaChallengeKind,
} from '../../models/feasibility/challenging-quota.model';

/** Shown on every qualification that an improbable quota's criteria names. */
export const IMPROBABLE_QUALIFICATION_WARNING =
  'One or more of these Qualifications/Quotas are contributing to the improbable quota(s). Please review these quotas before launching this survey.';

/** Shown on each improbable row inside the nested-quota table. */
export const IMPROBABLE_NESTED_QUOTA_ROW_WARNING =
  'This has been flagged as a highly challenging quota. Please consider adjusting quotas or targeting to broaden your search.';

export interface QuotaChallengeCopy {
  readonly message: string;
  readonly actionLabel: string;
  readonly drawerTitle: string;
}

interface QuotaChallengeCopyTemplate {
  readonly single: (qualificationLabel: string) => string;
  readonly many: (count: number) => string;
  readonly actionLabel: string;
  readonly noun: string;
}

const COPY: Record<QuotaChallengeKind, QuotaChallengeCopyTemplate> = {
  difficult: {
    single: (qualificationLabel) =>
      `${qualificationLabel} may be a difficult quota to field. Adjusting quotas or targeting to broaden the search can improve fielding.`,
    many: (count) =>
      `${count} highlighted quotas have been flagged as difficult quotas to field. We recommend adjusting quota criteria to broaden the search to improve fielding.`,
    actionLabel: 'See Difficult Quota(s)',
    noun: 'difficult quotas',
  },
  improbable: {
    single: (qualificationLabel) =>
      `${qualificationLabel} has been flagged as a highly challenging quota. Please consider adjusting quotas or targeting to broaden your search.`,
    many: (count) =>
      `${count} red quotas have been flagged as highly challenging. Please consider adjusting quotas or targeting to broaden your search.`,
    actionLabel: 'See improbable Quota(s)',
    noun: 'improbable quotas',
  },
};

/**
 * Banner and drawer wording for a set of challenging quotas. A single quota is
 * named by its qualifications; several are counted.
 *
 * `drawerTitle` is deliberately not pluralised — legacy renders "1 difficult
 * quotas", and the copy is kept verbatim so the ported screens read the same.
 * Change it only as a product decision, not as a typo fix.
 */
export function quotaChallengeCopy(
  kind: QuotaChallengeKind,
  quotas: readonly ChallengingQuota[],
): QuotaChallengeCopy {
  const template = COPY[kind];
  const [firstQuota] = quotas;

  return {
    message:
      quotas.length === 1 && firstQuota
        ? template.single(firstQuota.qualificationLabel)
        : template.many(quotas.length),
    actionLabel: template.actionLabel,
    drawerTitle: `${quotas.length} ${template.noun}`,
  };
}
